import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading, Modal } from "src/components/common";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { ModalSize } from "src/enumerations/ModalSize";
import { TrialInstitutionContractsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "src/helpers/date";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import * as TrialInstitutionContractActions from "../../../actions/trialInstitutionContract";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialInstitutionSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./TrialInstitutionContractsPage.scss";

interface TrialInstitutionContractsPageProps {
    trialInstitutionContracts: Dtos.TrialInstitutionContract[];
    loadingTrialInstitutionContracts: boolean;
    loadTrialInstitutionContractsSuccess: boolean;
    loadTrialInstitutionContractsFailure: boolean;
    loadTrialInstitutionContractsErrorCode: string;
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    deletingTrialInstitutionContract: boolean;
    deleteTrialInstitutionContractSuccess: boolean;
    clearTrialInstitutionContracts: TrialInstitutionContractActions.ITrialInstitutionContractClearActionCreator;
    loadTrialInstitutionContracts: TrialInstitutionContractActions.ITrialInstitutionContractLoadByTrialInstitutionIdActionCreator;
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    deleteTrialInstitutionContract: TrialInstitutionContractActions.ITrialInstitutionContractDeleteActionCreator;
}

const TrialInstitutionContractsPage: FC<TrialInstitutionContractsPageProps> = ({
    trialInstitutionContracts,
    loadingTrialInstitutionContracts,
    loadTrialInstitutionContractsSuccess,
    loadTrialInstitutionContractsFailure,
    loadTrialInstitutionContractsErrorCode,
    trialInstitution,
    loadingTrialInstitution,
    loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    permissions,
    pageSize,
    deletingTrialInstitutionContract,
    deleteTrialInstitutionContractSuccess,
    clearTrialInstitutionContracts,
    loadTrialInstitutionContracts,
    clearTrialInstitution,
    loadTrialInstitution,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize,
    deleteTrialInstitutionContract
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [tempTrialInstitutionContract, setTempTrialInstitutionContract] =
        useState<Dtos.TrialInstitutionContract | undefined>(undefined);

    //
    // -- Variables --
    const showTrialInstitutionContracts =
        !loadingTrialInstitutionContracts &&
        loadTrialInstitutionContractsSuccess &&
        !loadingTrialInstitution &&
        loadTrialInstitutionSuccess;


    // Load Trial institution & contracts
    useEffect(() => {
        if (trialInstitutionIdNumber && !trialInstitutionContracts) {
            setBreadcrumbs(
                TrialInstitutionContractsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber
                )
            );

            loadTrialInstitutionContracts(trialInstitutionIdNumber);
            loadTrialInstitution(trialInstitutionIdNumber);
        }
    }, [
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitutionContracts,
        setBreadcrumbs,
        loadTrialInstitutionContracts,
        loadTrialInstitution,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialInstitutionContractsFailure) {
            navigate(`/error/${loadTrialInstitutionContractsErrorCode}`);
        }
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`);
        }
    }, [
        loadTrialInstitutionContractsFailure,
        loadTrialInstitutionFailure,
        loadTrialInstitutionContractsErrorCode,
        loadTrialInstitutionErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            trialInstitutionIdNumber &&
            trialInstitution?.trial &&
            trialInstitution?.institution
        ) {
            setDrawerItems(
                TrialInstitutionSubMenu(trialInstitution, permissions)
            );
            setBreadcrumbs(
                TrialInstitutionContractsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    trialInstitution.trial?.displayName,
                    trialInstitution.institution?.name
                )
            );
        }
    }, [
        trialIdNumber,
        trialInstitution,
        trialInstitutionIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
    ]);

    // Cleanup effect to clear the trial institution & contracts on component unmount
    useEffect(() => {
        return () => {
            clearTrialInstitution();
            clearTrialInstitutionContracts();
        };
    }, [clearTrialInstitution, clearTrialInstitutionContracts]);

    // Reload trial institution & contracts after delete
    useEffect(() => {
        if (
            !deletingTrialInstitutionContract &&
            deleteTrialInstitutionContractSuccess
        ) {
            loadTrialInstitution(trialInstitutionIdNumber);
            loadTrialInstitutionContracts(trialInstitutionIdNumber);
        }
    }, [
        deletingTrialInstitutionContract,
        deleteTrialInstitutionContractSuccess,
        loadTrialInstitution,
        loadTrialInstitutionContracts,
        trialInstitutionIdNumber,
    ]);

    const generateDeleteButton = (
        trialInstitutionContract: Dtos.TrialInstitutionContract
    ) => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialInstitutionContractUpdate,
            undefined,
            undefined
        );

        if (!hasPermission) return null;

        return (
            <button
                key={`contractDeleteButton-${trialInstitutionContract.id}`}
                className="btn btn-outline-secondary btn-sm btn-row-version"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setTempTrialInstitutionContract(trialInstitutionContract);
                    setOpenModal(true);
                }}
            >
                <FontAwesomeIcon
                    icon={FontAwesomeIcons.Duotone.TRASH}
                    fixedWidth
                />
            </button>
        );
    };

    const createTable = useMemo(() => {
        let tableData = trialInstitutionContracts;

        if (!tableData || tableData?.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no trial institution contracts
                            to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = tableData.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={tableData}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : tableData.length
                    }
                    pageSize={showPagination ? pageSize : tableData.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no trial institution contracts to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/contracts/${rowInfo.original.id}/details`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.INSTITUTION}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Name",
                            accessor: "name",
                        },
                        {
                            id: "dateSentToSite",
                            Header: "Date Sent To Site",
                            accessor: "dateSentToSite",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "dateConfirmedBySite",
                            Header: "Date Sent To Site",
                            accessor: "dateConfirmedBySite",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "deleteContract",
                            Header: " ",
                            maxWidth: 75,
                            Cell: (props) => (
                                <div>
                                    {generateDeleteButton(props.original)}
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [
        trialInstitution,
        trialInstitutionContracts,
        pageSize,
        PAGE_SIZE_OPTIONS,
        navigate,
        setPageSize,
        trialIdNumber,
        trialInstitutionIdNumber,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-6 mb-2"}>
                        <h1>Contracts</h1>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialInstitutionContractCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/trials/${trialIdNumber}/trialInstitution/${trialInstitutionIdNumber}/contracts/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrialInstitutionContracts ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialInstitutionContracts ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showTrialInstitutionContracts,
        createTable,
        trialIdNumber,
        trialInstitutionIdNumber,
    ]);

    const renderDeleteConfirmation = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialInstitutionContractUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            return (
                <Modal
                    open={openModal}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setOpenModal(false);
                        setTempTrialInstitutionContract(undefined);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h2 className="mb-0">Delete</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setOpenModal(false);
                                setTempTrialInstitutionContract(undefined);
                            }}
                        >
                            <FontAwesomeIcon
                                fixedWidth
                                icon={FontAwesomeIcons.Regular.TIMES}
                            />
                        </button>
                    </div>
                    <div className="modal-body">
                        <span>
                            Are you sure you want to delete the contract "
                            <em>{tempTrialInstitutionContract?.name}"</em> from
                            trial institution{" "}
                            <strong>
                                {trialInstitution?.trial?.displayName} -{" "}
                                {trialInstitution?.institution?.name}
                            </strong>
                            .
                        </span>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                deleteTrialInstitutionContract(
                                    tempTrialInstitutionContract?.id
                                );
                                setOpenModal(false);
                                setTempTrialInstitutionContract(undefined);
                            }}
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                                setOpenModal(false);
                                setTempTrialInstitutionContract(undefined);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        trialInstitution,
        tempTrialInstitutionContract,
        openModal,
        permissions,
        setOpenModal,
        setTempTrialInstitutionContract,
        trialInstitutionIdNumber,
        loadTrialInstitutionContracts,
        deleteTrialInstitutionContract,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialInstitutionContractView}
            loading={
                loadingTrialInstitutionContracts || loadingTrialInstitution
            }
        >
            {renderContent}
            {renderDeleteConfirmation}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialInstitutionContractsPageProps> => {
    let trialInstitutionContracts: Dtos.TrialInstitutionContract[] | undefined =
        state.trialInstitutionContract.formData instanceof Array
            ? state.trialInstitutionContract.formData
            : undefined;
    let trialInstitution: Dtos.TrialInstitution | undefined = !(
        state.trialInstitution.formData instanceof Array
    )
        ? state.trialInstitution.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialInstitutionContracts: trialInstitutionContracts!,
        loadingTrialInstitutionContracts:
            state.trialInstitutionContract.loadState &&
            state.trialInstitutionContract.loadState.status ==
                RequestState.Pending,
        loadTrialInstitutionContractsSuccess:
            state.trialInstitutionContract.loadState &&
            state.trialInstitutionContract.loadState.status ==
                RequestState.Success,
        loadTrialInstitutionContractsFailure:
            state.trialInstitutionContract.loadState &&
            state.trialInstitutionContract.loadState.status ==
                RequestState.Failure,
        loadTrialInstitutionContractsErrorCode:
            state.trialInstitutionContract.loadState.errorCode,
        trialInstitution: trialInstitution!,
        loadingTrialInstitution:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode:
            state.trialInstitution.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        deletingTrialInstitutionContract:
            state.trialInstitutionContract.deleteState &&
            state.trialInstitutionContract.deleteState.status ==
                RequestState.Pending,
        deleteTrialInstitutionContractSuccess:
            state.trialInstitutionContract.deleteState &&
            state.trialInstitutionContract.deleteState.status ==
                RequestState.Success,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialInstitutionContractsPageProps> => {
    return {
        loadTrialInstitutionContracts: bindActionCreators(
            TrialInstitutionContractActions.LoadTrialInstitutionContractByTrialInstitutionId,
            dispatch
        ),
        clearTrialInstitutionContracts: bindActionCreators(
            TrialInstitutionContractActions.Clear,
            dispatch
        ),
        loadTrialInstitution: bindActionCreators(
            TrialInstitutionActions.LoadTrialInstitutionById,
            dispatch
        ),
        clearTrialInstitution: bindActionCreators(
            TrialInstitutionActions.Clear,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        deleteTrialInstitutionContract: bindActionCreators(
            TrialInstitutionContractActions.DeleteTrialInstitutionContract,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialInstitutionContractsPage);
